/* SearchAndFilter — composable facet filter bar (Shopify-style).
   A single input where clicking reveals a facet menu, each facet drills into
   a value selector, each selection becomes a removable chip.  Free-text
   search still works. Host passes `facets` (schema) and manages `filters` and
   `search` state; this component is presentation + composition only.

   Facet schema:
     { id: 'status', label: 'Status', icon: Icon, type: 'multi'|'numeric'|'date'|'boolean',
       options?: [{value, label}]              // required when type==='multi'
       numericLabel?: string                   // e.g. '$' for Total
     }

   Filters object shape (what the host receives back):
     { status: { values: ['confirm','pack'] },
       total:  { op: 'gt', value: 10000 },
       date:   { op: 'last7' },                // last7 | last30 | before | after | between
       overdue:{ on: true } }
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search, X, ChevronDown, Plus, Star, Check, Filter,
} from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import api from "../lib/api";

const OP_LABEL = {
  gt: "greater than", lt: "less than", between: "between",
  last7: "last 7 days", last30: "last 30 days",
  before: "before", after: "after",
  on: "is on",
};

function chipSummary(facet, val) {
  if (!val) return "";
  if (facet.type === "multi") {
    const labels = (val.values || []).map(v => facet.options.find(o => o.value === v)?.label || v);
    if (labels.length === 0) return `${facet.label} · any`;
    if (labels.length <= 2) return `${facet.label}: ${labels.join(", ")}`;
    return `${facet.label}: ${labels[0]} +${labels.length - 1}`;
  }
  if (facet.type === "numeric") {
    if (val.op === "between") return `${facet.label}: ${facet.numericLabel || ""}${val.min} – ${facet.numericLabel || ""}${val.max}`;
    return `${facet.label} ${OP_LABEL[val.op] || val.op} ${facet.numericLabel || ""}${val.value}`;
  }
  if (facet.type === "date") {
    if (val.op === "between") return `${facet.label}: ${val.from} → ${val.to}`;
    if (val.op === "last7" || val.op === "last30") return `${facet.label}: ${OP_LABEL[val.op]}`;
    return `${facet.label} ${OP_LABEL[val.op] || val.op} ${val.value || ""}`;
  }
  if (facet.type === "boolean") return val.on ? facet.label : `Not ${facet.label.toLowerCase()}`;
  return facet.label;
}

/**
 * Props:
 *   page              string           — 'seller_orders' | 'buyer_orders' (for saved views)
 *   facets            Array<facetDef>
 *   filters           object           — current filter map
 *   onChange(filters) callback
 *   search            string           — free-text controlled value
 *   onSearchChange(q) callback
 *   totalCount        number           — rows currently matching (for the "N results" hint)
 */
export default function SearchAndFilter({
  page, facets = [], filters = {}, onChange,
  search = "", onSearchChange, totalCount,
}) {
  const [open, setOpen] = useState(false);
  const [activeFacet, setActiveFacet] = useState(null); // when drilling into a facet
  const [savedViews, setSavedViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [newName, setNewName] = useState("");

  // Fetch saved views on mount and whenever `page` changes — no lazy guard so
  // the saved-views chip row is visible immediately after reload or save.
  const fetchSaved = useCallback(async () => {
    if (!page) return;
    try {
      const { data } = await api.get(`/saved-views?page=${page}`);
      setSavedViews(data || []);
    } catch { /* silent */ }
  }, [page]);

  useEffect(() => { fetchSaved(); }, [fetchSaved]);

  const appliedFacets = useMemo(
    () => facets.filter(f => filters[f.id] != null).map(f => ({ facet: f, val: filters[f.id] })),
    [facets, filters],
  );

  const unappliedFacets = facets.filter(f => filters[f.id] == null);

  const setFacet = (facetId, value) => {
    const next = { ...filters };
    if (value == null) delete next[facetId]; else next[facetId] = value;
    onChange(next);
  };

  const clearAll = () => onChange({});

  // ── Save view ──
  const saveCurrent = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Give the view a name");
    try {
      await api.post("/saved-views", { page, name, filters: { search, ...filters } });
      toast.success(`Saved view · ${name}`);
      setSaveOpen(false);
      setNewName("");
      await fetchSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  const applyView = (v) => {
    const { search: s, ...rest } = v.filters || {};
    onChange(rest);
    if (onSearchChange) onSearchChange(s || "");
  };

  const removeView = async (v) => {
    try {
      await api.delete(`/saved-views/${v.id}`);
      setSavedViews(prev => prev.filter(x => x.id !== v.id));
    } catch { toast.error("Could not remove"); }
  };

  const hasAnything = appliedFacets.length > 0 || search.length > 0;

  return (
    <div className="space-y-2" data-testid="search-and-filter">
      {/* Saved-view chips row (hidden when none saved) */}
      {savedViews.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/45 inline-flex items-center gap-1.5 mr-1">
            <Star className="w-3 h-3"/>Saved views
          </span>
          {savedViews.map(v => (
            <div
              key={v.id}
              data-testid={`saved-view-${v.id}`}
              className="group inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full text-xs border border-border bg-white hover:border-foreground/30 transition-colors"
            >
              <button onClick={() => applyView(v)} className="inline-flex items-center gap-1">
                <Star className="w-3 h-3"/>{v.name}
              </button>
              <button
                onClick={() => removeView(v)}
                data-testid={`remove-view-${v.id}`}
                className="w-4 h-4 rounded-full hover:bg-secondary grid place-items-center ml-0.5"
                aria-label="Remove saved view"
              >
                <X className="w-2.5 h-2.5"/>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search + filter bar (Shopify-style single pill) */}
      <div className="flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1.5" data-testid="filter-bar">
        {/* Search input */}
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-foreground/40 pointer-events-none"/>
          <Input
            value={search}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="Search and filter…"
            data-testid="filter-search-input"
            className="pl-7 h-8 border-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
          />
        </div>

        {/* Applied filter chips INSIDE the bar */}
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          {appliedFacets.map(({ facet, val }) => (
            <Popover key={facet.id}>
              <PopoverTrigger asChild>
                <button
                  data-testid={`applied-${facet.id}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] bg-foreground/[0.06] border border-foreground/20 hover:bg-foreground/[0.1] transition-colors max-w-[240px]"
                >
                  <facet.icon className="w-3 h-3 shrink-0"/>
                  <span className="truncate">{chipSummary(facet, val)}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-0" data-testid={`edit-${facet.id}-panel`}>
                <FacetEditor
                  facet={facet}
                  value={val}
                  onApply={(v) => setFacet(facet.id, v)}
                  onClear={() => setFacet(facet.id, null)}
                />
              </PopoverContent>
            </Popover>
          ))}
        </div>

        {/* Add-filter trigger */}
        <Popover
          open={open}
          onOpenChange={(v) => {
            setOpen(v);
            if (v) setActiveFacet(null);
          }}
        >
          <PopoverTrigger asChild>
            <button
              data-testid="add-filter-btn"
              className="shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] border border-dashed border-foreground/30 text-foreground/75 hover:text-foreground hover:border-foreground/50 transition-colors"
            >
              <Filter className="w-3 h-3"/>Add filter
              <ChevronDown className="w-3 h-3 opacity-60"/>
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0" data-testid="add-filter-panel">
            {activeFacet ? (
              <div>
                <div className="px-3 py-2 border-b border-border flex items-center gap-2">
                  <button
                    onClick={() => setActiveFacet(null)}
                    className="text-[11px] text-foreground/60 hover:text-foreground inline-flex items-center gap-1"
                  >
                    <ChevronDown className="w-3 h-3 rotate-90"/>Back
                  </button>
                  <span className="text-sm font-medium">{activeFacet.label}</span>
                </div>
                <FacetEditor
                  facet={activeFacet}
                  value={filters[activeFacet.id]}
                  onApply={(v) => { setFacet(activeFacet.id, v); setActiveFacet(null); setOpen(false); }}
                  onClear={() => { setFacet(activeFacet.id, null); setActiveFacet(null); setOpen(false); }}
                />
              </div>
            ) : (
              <div className="py-1 max-h-96 overflow-y-auto" data-testid="facet-picker">
                {unappliedFacets.length === 0 && appliedFacets.length > 0 && (
                  <div className="px-3 py-4 text-center text-[11px] text-foreground/55">
                    All filters applied. Remove one to add another.
                  </div>
                )}
                {unappliedFacets.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setActiveFacet(f)}
                    data-testid={`facet-${f.id}`}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-secondary/40 transition-colors text-left"
                  >
                    <f.icon className="w-3.5 h-3.5 text-foreground/60"/>
                    <span className="flex-1">{f.label}</span>
                    <ChevronDown className="w-3 h-3 -rotate-90 opacity-40"/>
                  </button>
                ))}
                {page && hasAnything && (
                  <div className="border-t border-border mt-1 px-3 py-2">
                    <button
                      onClick={() => { setOpen(false); setSaveOpen(true); }}
                      data-testid="save-view-btn"
                      className="w-full text-left text-xs inline-flex items-center gap-1.5 text-foreground/70 hover:text-foreground"
                    >
                      <Star className="w-3 h-3"/>Save this view…
                    </button>
                  </div>
                )}
              </div>
            )}
          </PopoverContent>
        </Popover>

        {hasAnything && (
          <button
            onClick={() => { clearAll(); onSearchChange?.(""); }}
            data-testid="clear-filters-btn"
            className="shrink-0 text-[11px] text-foreground/55 hover:text-foreground underline-offset-2 hover:underline pr-1"
          >
            Clear all
          </button>
        )}
      </div>

      {typeof totalCount === "number" && (
        <div className="text-[11px] text-foreground/55">
          <span className="font-medium text-foreground">{totalCount}</span> {totalCount === 1 ? "result" : "results"}
          {hasAnything && (
            <span className="ml-2 text-foreground/40">· filtered</span>
          )}
        </div>
      )}

      {/* Save-view dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm" data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle className="display text-xl tracking-tight">Save this view</DialogTitle>
            <DialogDescription className="text-xs text-foreground/55">
              Save the current filter combination so you can jump back to it with one click.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveCurrent()}
            placeholder="e.g. Big Apex orders this week"
            data-testid="save-view-name"
            className="h-11 mt-2"
          />
          <Button
            onClick={saveCurrent}
            data-testid="save-view-submit"
            className="mt-3 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90"
          >
            Save view
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FacetEditor — renders the value picker for a single facet.
// ─────────────────────────────────────────────────────────────────────────────
function FacetEditor({ facet, value, onApply, onClear }) {
  if (facet.type === "multi")    return <MultiEditor   facet={facet} value={value} onApply={onApply} onClear={onClear}/>;
  if (facet.type === "numeric")  return <NumericEditor facet={facet} value={value} onApply={onApply} onClear={onClear}/>;
  if (facet.type === "date")     return <DateEditor    facet={facet} value={value} onApply={onApply} onClear={onClear}/>;
  if (facet.type === "boolean")  return <BooleanEditor facet={facet} value={value} onApply={onApply} onClear={onClear}/>;
  return null;
}

function MultiEditor({ facet, value, onApply, onClear }) {
  const [selected, setSelected] = useState(value?.values || []);
  const toggle = (v) => setSelected(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  return (
    <div className="p-2">
      <div className="max-h-64 overflow-y-auto">
        {facet.options.map(o => {
          const checked = selected.includes(o.value);
          return (
            <button
              key={o.value}
              onClick={() => toggle(o.value)}
              data-testid={`option-${facet.id}-${o.value}`}
              className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm hover:bg-secondary/40 text-left"
            >
              <span className={`w-4 h-4 rounded border grid place-items-center ${checked ? "bg-foreground border-foreground text-background" : "border-border bg-white"}`}>
                {checked && <Check className="w-2.5 h-2.5"/>}
              </span>
              <span className="flex-1 truncate">{o.label}</span>
            </button>
          );
        })}
      </div>
      <FacetFooter
        applyEnabled={selected.length > 0}
        onApply={() => onApply({ values: selected })}
        onClear={onClear}
      />
    </div>
  );
}

function NumericEditor({ facet, value, onApply, onClear }) {
  const [op, setOp] = useState(value?.op || "gt");
  const [v1, setV1] = useState(value?.value ?? value?.min ?? "");
  const [v2, setV2] = useState(value?.max ?? "");
  const valid = op === "between"
    ? v1 !== "" && v2 !== "" && Number(v1) <= Number(v2)
    : v1 !== "";
  return (
    <div className="p-3 space-y-2">
      <div className="flex gap-1 flex-wrap">
        {[["gt", "Greater than"], ["lt", "Less than"], ["between", "Between"]].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setOp(k)}
            data-testid={`op-${facet.id}-${k}`}
            className={`text-[11px] px-2.5 py-1 rounded-full border ${op === k ? "bg-foreground text-background border-foreground" : "bg-white text-foreground/75 border-border hover:border-foreground/30"}`}
          >{l}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          {facet.numericLabel && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40 text-sm">{facet.numericLabel}</span>}
          <Input
            type="number"
            value={v1}
            onChange={(e) => setV1(e.target.value)}
            placeholder={op === "between" ? "min" : "value"}
            data-testid={`numeric-${facet.id}-v1`}
            className={`h-10 ${facet.numericLabel ? "pl-7" : ""}`}
          />
        </div>
        {op === "between" && (
          <div className="relative flex-1">
            {facet.numericLabel && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40 text-sm">{facet.numericLabel}</span>}
            <Input
              type="number"
              value={v2}
              onChange={(e) => setV2(e.target.value)}
              placeholder="max"
              data-testid={`numeric-${facet.id}-v2`}
              className={`h-10 ${facet.numericLabel ? "pl-7" : ""}`}
            />
          </div>
        )}
      </div>
      <FacetFooter
        applyEnabled={valid}
        onApply={() => onApply(op === "between" ? { op, min: Number(v1), max: Number(v2) } : { op, value: Number(v1) })}
        onClear={onClear}
      />
    </div>
  );
}

function DateEditor({ facet, value, onApply, onClear }) {
  const [op, setOp] = useState(value?.op || "last7");
  const [from, setFrom] = useState(value?.from || "");
  const [to, setTo] = useState(value?.to || "");
  const [single, setSingle] = useState(value?.value || "");
  const valid =
    op === "last7" || op === "last30" ||
    ((op === "before" || op === "after") && !!single) ||
    (op === "between" && !!from && !!to);
  return (
    <div className="p-3 space-y-2">
      <div className="flex gap-1 flex-wrap">
        {[["last7", "Last 7 days"], ["last30", "Last 30 days"], ["between", "Between"], ["before", "Before"], ["after", "After"]].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setOp(k)}
            data-testid={`op-${facet.id}-${k}`}
            className={`text-[11px] px-2.5 py-1 rounded-full border ${op === k ? "bg-foreground text-background border-foreground" : "bg-white text-foreground/75 border-border hover:border-foreground/30"}`}
          >{l}</button>
        ))}
      </div>
      {(op === "before" || op === "after") && (
        <Input type="date" value={single} onChange={(e) => setSingle(e.target.value)} className="h-10" data-testid={`date-${facet.id}-single`}/>
      )}
      {op === "between" && (
        <div className="flex gap-2">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 flex-1" data-testid={`date-${facet.id}-from`}/>
          <Input type="date" value={to}   onChange={(e) => setTo(e.target.value)}   className="h-10 flex-1" data-testid={`date-${facet.id}-to`}/>
        </div>
      )}
      <FacetFooter
        applyEnabled={valid}
        onApply={() => {
          if (op === "last7" || op === "last30") onApply({ op });
          else if (op === "between") onApply({ op, from, to });
          else onApply({ op, value: single });
        }}
        onClear={onClear}
      />
    </div>
  );
}

function BooleanEditor({ facet, value, onApply, onClear }) {
  const [on, setOn] = useState(value?.on ?? true);
  return (
    <div className="p-3 space-y-2">
      <div className="flex gap-1">
        <button
          onClick={() => setOn(true)}
          data-testid={`bool-${facet.id}-true`}
          className={`flex-1 text-xs py-2 rounded-md border ${on ? "bg-foreground text-background border-foreground" : "bg-white border-border"}`}
        >Yes — {facet.label.toLowerCase()}</button>
        <button
          onClick={() => setOn(false)}
          data-testid={`bool-${facet.id}-false`}
          className={`flex-1 text-xs py-2 rounded-md border ${!on ? "bg-foreground text-background border-foreground" : "bg-white border-border"}`}
        >No — exclude</button>
      </div>
      <FacetFooter applyEnabled onApply={() => onApply({ on })} onClear={onClear}/>
    </div>
  );
}

function FacetFooter({ applyEnabled, onApply, onClear }) {
  return (
    <div className="flex gap-2 pt-2 border-t border-border mt-2">
      <button
        onClick={onClear}
        data-testid="facet-clear"
        className="flex-1 text-[11px] py-2 rounded-full border border-border bg-white text-foreground/70 hover:text-foreground hover:border-foreground/30"
      >Clear</button>
      <button
        onClick={onApply}
        disabled={!applyEnabled}
        data-testid="facet-apply"
        className="flex-1 text-[11px] py-2 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1"
      >
        <Plus className="w-3 h-3"/>Apply
      </button>
    </div>
  );
}
